import { Component, computed, inject, output, signal } from '@angular/core';
import { InputFieldComponent } from 'libs/shared/src/lib/components/input-field/input-field.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GenericDropdownComponent } from 'libs/shared/src/lib/components/generic-dropdown/generic-dropdown.component';
import { DropdownData } from 'libs/shared/src/lib/models/dropdown.models';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'apps/cinari-erp-client/src/app/store/app.reducers';
import { selectCategoryListByProductListId, selectProductList, selectSubcategories } from '@zayan-erp/stores';
import { ProductService } from '../../services/product.service';
import { AsyncPipe, DecimalPipe, SlicePipe } from '@angular/common';
import { Product, ProductCreate } from '../../models/product.model';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FileDropComponent,
  FileUploadServiceV2,
  ImagePreview,
  ImagePreviewItem,
  rxResourceFixed
} from '@zayan-erp/shared';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { finalize, forkJoin, map, of, switchMap, tap } from 'rxjs';
import * as HomeBuildingActions from '@zayan-erp/stores';
import { LookupsService } from 'libs/shared/src/lib/services/lookups.service';
@Component({
  selector: 'lib-product-form',
  imports: [
    InputFieldComponent,
    NgSelectModule,
    GenericDropdownComponent,
    ToggleSwitchModule,
    ImagePreview,
    ReactiveFormsModule,
    AsyncPipe,
    DecimalPipe,
    FileDropComponent,
    SlicePipe
  ],
  templateUrl: './product-form.component.html',
  styleUrl: './product-form.component.scss'
})
export class ProductFormComponent {
  #activatedRoute = inject(ActivatedRoute);
  #store = inject(Store<AppState>);
  #productService = inject(ProductService);
  #router = inject(Router);
  #lookupsService = inject(LookupsService);
  #fileUploadV2 = inject(FileUploadServiceV2);

  productImageItems = signal<{ documentId: string; name: string; size: number; url: string }[]>([]);
  reuploadAt: number | null = null;

  productPreviewImages = computed((): ImagePreviewItem[] =>
    this.productImageItems()
      .filter(item => !!item.url)
      .map(item => ({
        itemImageSrc: item.url,
        thumbnailImageSrc: item.url,
        alt: item.name
      }))
  );

  productClassName = output<string>();
  title = output<string>();
  subcategoryId = output<string>();

  selectedProductId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));
  selectedProductListId = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('productListId'));
  selectedCategoryId = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('categoryId'));
  selectedSubcategoryId = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('subcategoryId'));
  productList$ = this.#store.select(selectProductList);
  category$ = this.#store.select(selectCategoryListByProductListId(this.selectedProductListId()!));
  subcategory$ = this.#store.select(
    selectSubcategories(this.#activatedRoute.snapshot.queryParamMap.get('categoryId')!)
  );

  productClass = toSignal(this.#productService.readProductClasses(), { initialValue: [] as DropdownData[] });
  taxCategory = toSignal(this.#productService.readTaxCategories(), { initialValue: [] as DropdownData[] });
  tasks = toSignal(this.#lookupsService.readTasks(), { initialValue: [] as DropdownData[] });
  locations = toSignal(this.#lookupsService.readLocations(), { initialValue: [] as DropdownData[] });
  UOFM = toSignal(this.#lookupsService.readUOMs(), { initialValue: [] as DropdownData[] });
  UOFMList = computed(() => this.UOFM() ?? []);

  productRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedProductId()! }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#productService.readProductById(params.id).pipe(
          tap(product => {
            if (product) {
              this.editForm(product);
              this.title.emit(product.code);
              this.productClassName.emit(product.productClass.name.toLowerCase());
            }
          })
        );
      }
      return of(null);
    }
  });

  constructor() {
    this.productForm.get('salesOffice')?.valueChanges.subscribe(salesOffice => {
      const designCenterControl = this.productForm.get('designCenter');
      if (salesOffice) {
        designCenterControl?.removeValidators([Validators.requiredTrue]);
      } else {
        designCenterControl?.addValidators([Validators.requiredTrue]);
      }
      designCenterControl?.updateValueAndValidity({ emitEvent: false });
    });
    this.productForm.get('designCenter')?.valueChanges.subscribe(designCenter => {
      const salesOfficeControl = this.productForm.get('salesOffice');
      if (designCenter) {
        salesOfficeControl?.removeValidators([Validators.requiredTrue]);
      } else {
        salesOfficeControl?.addValidators([Validators.requiredTrue]);
      }
      salesOfficeControl?.updateValueAndValidity({ emitEvent: false });
    });

    this.productForm.get('appliesToAllLocations')?.valueChanges.subscribe(appliesToAllLocations => {
      const locationIdsControl = this.productForm.get('locationIds');
      if (appliesToAllLocations) {
        locationIdsControl?.setValue([]);
        locationIdsControl?.disable();
      } else {
        locationIdsControl?.enable();
      }
    });
    this.productForm.get('subcategoryId')?.valueChanges.subscribe(subcategoryId => {
      if (subcategoryId) {
        this.subcategoryId.emit(subcategoryId);
      }
    });

    if (this.selectedProductListId()) {
      this.#store.dispatch(
        HomeBuildingActions.loadCategoryListByProductListId({ productListId: this.selectedProductListId()! })
      );
      this.category$ = this.#store.select(selectCategoryListByProductListId(this.selectedProductListId()!));
    }

    if (this.selectedCategoryId()) {
      this.#store.dispatch(
        HomeBuildingActions.loadSubcategories({
          productListId: this.selectedProductListId()!,
          categoryListId: this.selectedCategoryId()!
        })
      );
      this.subcategory$ = this.#store.select(selectSubcategories(this.selectedCategoryId()!));
    }

    this.productForm.get('productListId')?.valueChanges.subscribe(productListId => {
      this.productForm.get('categoryId')?.setValue(null);
      if (productListId) {
        this.#store.dispatch(HomeBuildingActions.loadCategoryListByProductListId({ productListId: productListId }));
        this.category$ = this.#store.select(selectCategoryListByProductListId(productListId!));
      }
    });

    this.productForm.get('categoryId')?.valueChanges.subscribe(categoryId => {
      this.productForm.get('subcategoryId')?.setValue(null);
      if (categoryId) {
        this.#store.dispatch(
          HomeBuildingActions.loadSubcategories({
            productListId: this.selectedProductListId()!,
            categoryListId: categoryId
          })
        );
      }
      this.subcategory$ = this.#store.select(selectSubcategories(categoryId!));
    });
  }

  productForm = new FormGroup({
    //product details
    code: new FormControl<string | null>(null, [Validators.required]),
    uofmId: new FormControl<string | null>(null),
    cutoffTaskId: new FormControl<string | null>(null),
    locationIds: new FormControl<string[]>([]),
    //product description
    description: new FormControl<string | null>(null),
    salesDescription: new FormControl<string | null>(null),
    //List or categories
    productListId: new FormControl<string | null>(
      this.#activatedRoute.snapshot.queryParamMap.get('productListId') ?? null,
      [Validators.required]
    ),
    categoryId: new FormControl<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('categoryId') ?? null, [
      Validators.required
    ]),
    subcategoryId: new FormControl<string | null>(
      this.#activatedRoute.snapshot.queryParamMap.get('subcategoryId') ?? null,
      [Validators.required]
    ),
    taxCategoryId: new FormControl<string | null>(null, [Validators.required]),
    productClassId: new FormControl<string | null>(null, [Validators.required]),
    //Settings
    isGlobal: new FormControl<boolean>(false),
    sellable: new FormControl<boolean>(false),
    salesOffice: new FormControl<boolean>(false, [Validators.required, Validators.requiredTrue]),
    designCenter: new FormControl<boolean>(false, [Validators.required, Validators.requiredTrue]),
    selectionRequired: new FormControl<boolean>(false),
    appliesToAllLocations: new FormControl<boolean>(false),
    loadByDefault: new FormControl<boolean>(false),
    isActive: new FormControl<boolean>(false)
  });

  editForm(product: Product) {
    this.productForm.patchValue({
      code: product.code,
      uofmId: product.uofmId,
      cutoffTaskId: product.cutoffTaskId ?? null,
      locationIds: product.productLocations?.map(p => p.locationId),
      description: product.description ?? null,
      salesDescription: product.salesDescription ?? null,
      productListId: product.productListId,
      categoryId: product.subcategory.category.id,
      subcategoryId: product.subcategory.id,
      taxCategoryId: product.taxCategory?.id,
      productClassId: product.productClass.id,
      isGlobal: product.isGlobal,
      sellable: product.sellable,
      salesOffice: product.salesOffice,
      designCenter: product.designCenter,
      selectionRequired: product.selectionRequired,
      appliesToAllLocations: product.appliesToAllLocations,
      loadByDefault: product.loadByDefault,
      isActive: product.isActive
    });
    const rows = product.images?.length
      ? product.images.map(img => ({
          documentId: img.document.id,
          name: img.document.s3Key?.split('/').pop() ?? 'image',
          size: 0,
          url: img.document.directUrl ?? ''
        }))
      : (product.productImages ?? []).map((documentId: string) => ({
          documentId,
          name: 'image',
          size: 0,
          url: ''
        }));
    this.productImageItems.set(rows);

    // Update validators based on initial values
    const salesOfficeControl = this.productForm.get('salesOffice');
    const designCenterControl = this.productForm.get('designCenter');

    if (product.salesOffice) {
      designCenterControl?.removeValidators([Validators.requiredTrue]);
    } else {
      designCenterControl?.addValidators([Validators.requiredTrue]);
    }

    if (product.designCenter) {
      salesOfficeControl?.removeValidators([Validators.requiredTrue]);
    } else {
      salesOfficeControl?.addValidators([Validators.requiredTrue]);
    }

    salesOfficeControl?.updateValueAndValidity({ emitEvent: false });
    designCenterControl?.updateValueAndValidity({ emitEvent: false });
  }

  toggleProduct() {
    // this.#productService.toggleProduct(this.selectedProductId()!).subscribe(() => {
    //   this.productRxResource.reload();
    // });
  }

  private uploadOne(file: File) {
    return this.#fileUploadV2.generatePublicUploadUrl(file.name).pipe(
      switchMap(up =>
        this.#fileUploadV2
          .uploadFileToS3({
            fileName: file.name,
            presignedUrl: up.uploadUrl,
            s3Key: up.s3Key,
            sourceFile: file
          })
          .pipe(
            map(() => ({
              documentId: up.documentId,
              name: file.name,
              size: file.size,
              url: URL.createObjectURL(file)
            }))
          )
      )
    );
  }

  onFileDrop(files: NgxFileDropEntry[]) {
    const fileEntries = files.filter(f => f.fileEntry.isFile);
    if (!fileEntries.length) return;
    Promise.all(
      fileEntries.map(entry => new Promise<File>(resolve => (entry.fileEntry as FileSystemFileEntry).file(resolve)))
    ).then(fileList => {
      forkJoin(fileList.map(f => this.uploadOne(f))).subscribe(rows => {
        this.productImageItems.update(a => {
          const merged = [...a, ...rows];
          return [...new Map(merged.map(r => [r.documentId, r])).values()];
        });
      });
    });
  }

  onAddFiles(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;
    forkJoin(Array.from(files).map(f => this.uploadOne(f)))
      .pipe(finalize(() => (input.value = '')))
      .subscribe(rows => {
        this.productImageItems.update(a => {
          const merged = [...a, ...rows];
          return [...new Map(merged.map(r => [r.documentId, r])).values()];
        });
      });
  }

  onReuploadFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    const replaceIndex = this.reuploadAt;
    this.reuploadAt = null;
    if (!files?.length || replaceIndex === null) {
      input.value = '';
      return;
    }
    this.uploadOne(files[0])
      .pipe(finalize(() => (input.value = '')))
      .subscribe(row => {
        const prev = this.productImageItems()[replaceIndex];
        if (prev?.url.startsWith('blob:')) URL.revokeObjectURL(prev.url);
        this.productImageItems.update(a => {
          const n = [...a];
          n[replaceIndex] = row;
          return n;
        });
      });
  }

  onDrop(e: DragEvent) {
    e.preventDefault();
    const dt = e.dataTransfer?.files;
    if (!dt?.length) return;
    forkJoin(Array.from(dt).map(f => this.uploadOne(f))).subscribe(rows => {
      this.productImageItems.update(a => {
        const merged = [...a, ...rows];
        return [...new Map(merged.map(r => [r.documentId, r])).values()];
      });
    });
  }

  removeProductImage(i: number) {
    const row = this.productImageItems()[i];
    if (row?.url.startsWith('blob:')) URL.revokeObjectURL(row.url);
    this.productImageItems.update(a => a.filter((_, idx) => idx !== i));
  }

  submitProduct(navigate: boolean = false) {
    // Ensure form validity is up to date before checking
    this.productForm.updateValueAndValidity();

    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }
    const uniqueImages = [...new Map(this.productImageItems().map(i => [i.documentId, i])).values()].map(i => ({
      documentId: i.documentId
    }));
    const payload = {
      ...this.productForm.value,
      productImages: uniqueImages
    } as ProductCreate;
    if (this.selectedProductId()) {
      this.#productService.updateProduct(this.selectedProductId()!, payload).subscribe({
        next: () => {
          this.productRxResource.reload();
          this.afterSubmitSuccess(this.selectedProductId()!, navigate);
        }
      });
    } else {
      this.#productService.createProduct(payload).subscribe({
        next: res => {
          this.afterSubmitSuccess(res.id, navigate);
        }
      });
    }
  }
  afterSubmitSuccess(id?: string, navigate: boolean = false) {
    if (navigate) {
      this.#router.navigate(['/home-building/product-catalogue/products/', id]);
      const productClassId = this.productForm.get('productClassId')?.value;
      if (productClassId) {
        const productClass = this.productClass()?.find(item => item.id === productClassId);
        this.productClassName.emit(productClass?.firstLabel?.toLowerCase() ?? '');
      }
    } else {
      this.productForm.patchValue({
        code: null,
        salesDescription: null,
        description: null
      });
      this.productImageItems().forEach(r => {
        if (r.url.startsWith('blob:')) URL.revokeObjectURL(r.url);
      });
      this.productImageItems.set([]);
    }
  }
}
